import React, { useState, useEffect } from "react";
import { CaseLower, CaseSensitive, CaseUpper } from "lucide-react";
import { useSelector } from "react-redux";
import { getCanvasFonts } from "../../../utils/truInkTemplate.js";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Paper,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import axios from "axios";

const AVAILABLE_GROUPS = [
  { label: "None", value: "" },
  { label: "Ongoing Meeting", value: "ONGOING_MEETING" },
  { label: "Upcoming Meeting", value: "UPCOMING_MEETING" },
  { label: "Occupancy Indicator", value: "OCCUPANCY_INDICATOR" },
];

const MEETING_TEMPLATE_DATABINDING = [
  { label: "Meeting Title", value: "MEETING_TITLE" },
  { label: "Organizer", value: "ORGANIZER" },
  { label: "Start Time", value: "START_TIME" },
  { label: "End Time", value: "END_TIME" },
  { label: "Start TO End", value: "START_TO_END_UPPER" },
  { label: "Start To End", value: "START_TO_END" },
  { label: "Start - End", value: "START_DASH_END" },
  { label: "Till End", value: "TILL_END" },
  { label: "From Start", value: "FROM_START" },
  { label: "From Start to End", value: "FROM_START_TO_END" },
  { label: "Room Name", value: "ROOM_NAME" },
  { label: "Room Capacity", value: "ROOM_CAPACITY" },
  { label: "Current Occupancy", value: "CURRENT_OCCUPANCY" },
];
const calculateOptimalFontSizeEnhanced = (
  text,
  containerWidth,
  containerHeight,
  fontFamily,
  currentFontSize,
  lineHeight = 1.2,
) => {
  if (!text || !containerWidth || !containerHeight) return currentFontSize;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const fontFamilyToUse = fontFamily || "Arial";
  const calculateTextDimensions = (fontSize, text) => {
    context.font = `${fontSize}px "${fontFamilyToUse}", Arial, sans-serif`;
    const paragraphs = text.split("\n");
    let totalHeight = 0;
    let maxLineWidth = 0;

    paragraphs.forEach((paragraph) => {
      if (paragraph.trim() === "") {
        totalHeight += fontSize * lineHeight;
        return;
      }

      const words = paragraph.split(" ");
      let currentLine = words[0] || "";

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine + " " + word;
        const metrics = context.measureText(testLine);

        if (metrics.width <= containerWidth - 20) {
          currentLine = testLine;
        } else {
          maxLineWidth = Math.max(
            maxLineWidth,
            context.measureText(currentLine).width,
          );
          totalHeight += fontSize * lineHeight;
          currentLine = word;
        }
      }
      maxLineWidth = Math.max(
        maxLineWidth,
        context.measureText(currentLine).width,
      );
      totalHeight += fontSize * lineHeight;
    });

    return {
      width: maxLineWidth,
      height: totalHeight,
    };
  };

  let minSize = 6;
  let maxSize = 120;
  let optimalSize = currentFontSize;
  for (let i = 0; i < 10; i++) {
    const size = Math.floor((minSize + maxSize) / 2);
    const dimensions = calculateTextDimensions(size, text);

    if (
      dimensions.width <= containerWidth - 20 &&
      dimensions.height <= containerHeight - 10
    ) {
      minSize = size;
      optimalSize = size;
    } else {
      maxSize = size;
    }
  }

  return Math.floor(optimalSize);
};

const RightSidebar = ({
  selectedElement,
  canvasColor,
  dataBindingValue,
  updateElement,
  onDeleteElement,
  onDuplicate,
  onBringForward,
  onSendBackward,
  internalConfigs,
  truInkEMMSTemplate,
  rightSideRef,
}) => {
  const { tenantId, siteId } = useSelector((state) => {
    // Access the slice directly - it's already an object!
    const truInkData = state.truInk;

    if (!truInkData) {
      return { tenantId: null, siteId: null };
    }

    // Use optional chaining to safely get the IDs
    return {
      tenantId: truInkData.selectedSite?.tenantId || null,
      siteId: truInkData.selectedSite?.id || null,
    };
  });

  // --- 2. State for Dynamic Fonts ---
  const [availableFonts, setAvailableFonts] = useState([
    { label: "Arial", value: "Arial, sans-serif" }, // Default fallback
  ]);

  useEffect(() => {
    const loadFonts = async () => {
      // Note: If you want generic fonts to load even without tenantId,
      // you might want to adjust this check as we discussed earlier.
      if (!tenantId) return;

      try {
        const fonts = await getCanvasFonts(tenantId, siteId);

        const formattedFonts = await Promise.all(
          fonts.map(async (f) => {
            const manifestUrl = f.customProperties?.url;

            if (manifestUrl) {
              const fontId = `dynamic-font-${f.name.replace(/\s+/g, "-")}`;

              if (!document.getElementById(fontId)) {
                try {
                  const { data } = await axios.get(manifestUrl);

                  // Extract base path from manifest URL
                  const baseUrl = manifestUrl.substring(
                    0,
                    manifestUrl.lastIndexOf("/"),
                  );

                  const style = document.createElement("style");
                  style.id = fontId;

                  const fontFaces = data.fonts
                    .map(
                      (font) => `
            @font-face {
              font-family: '${data.family}';
              src: url('${baseUrl}/${font.file}') format('truetype');
              font-weight: ${font.weight};
              font-style: ${font.style};
              font-display: swap;
            }
          `,
                    )
                    .join("\n");

                  style.appendChild(document.createTextNode(fontFaces));
                  document.head.appendChild(style);
                 
                } catch (error) {
                  console.error("Failed loading font manifest:", error);
                }
              }
            }

            return {
              label: f.name,
              value: f.value,
            };
          }),
        );

        setAvailableFonts(formattedFonts);
      } catch (err) {
        console.error("Error loading fonts:", err);
        toast.error("Failed to load custom fonts");
      }
    };

    loadFonts();
  }, [tenantId,siteId]);
  const getCustomKeys = () => {
    if (!dataBindingValue || !Array.isArray(dataBindingValue)) return [];

    const customKeys = new Set();

    dataBindingValue.forEach((item) => {
      if (item.fields && typeof item.fields === "object") {
        Object.keys(item.fields).forEach((key) => {
          customKeys.add(key);
        });
      }
    });

    return Array.from(customKeys).sort();
  };

  const customKeys = getCustomKeys();
  const handleVerticalAlignmentChange = (alignment) => {
    updateElement(selectedElement.id, { verticalAlign: alignment });
  };
  const handleTextChange = (e) => {
    updateElement(selectedElement.id, { content: e.target.value });
  };

  const handleTextColorChange = (colorValue) => {
    updateElement(selectedElement.id, { fill: colorValue });
  };

  const handleQRColorChange = (colorValue) => {
    updateElement(selectedElement.id, { fill: colorValue });
  };

  const handleFontSizeChange = (e) => {
    updateElement(selectedElement.id, { fontSize: parseInt(e.target.value) });
  };

  const handleFontFamilyChange = (e) => {
    updateElement(selectedElement.id, { fontFamily: e.target.value });
  };

  const handleBoldToggle = () => {
    updateElement(selectedElement.id, {
      fontWeight: selectedElement.fontWeight === "bold" ? "normal" : "bold",
    });
  };

  const handleItalicToggle = () => {
    updateElement(selectedElement.id, {
      fontStyle: selectedElement.fontStyle === "italic" ? "normal" : "italic",
    });
  };

  const handleAlignmentChange = (alignment) => {
    updateElement(selectedElement.id, { textAlign: alignment });
  };

  const handleUrlChange = (e) => {
    updateElement(selectedElement.id, { url: e.target.value });
  };

  const handleShapeTypeChange = (e) => {
    updateElement(selectedElement.id, { shapeType: e.target.value });
  };

  const handleColorChange = (colorValue) => {
    updateElement(selectedElement.id, { fill: colorValue });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        updateElement(selectedElement.id, { src: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

 
  const handleDataKeyChange = (e) => {
    const newKey = e.target.value;
    updateElement(selectedElement.id, {
      attribute_binding: {
        ...selectedElement.attribute_binding,
        data_key: newKey,
      },
      // We set isDataBound to true so the canvas knows to use the bound value
      isDataBound: !!newKey,
    });
  };

  const handleMeetingNumberChange = (e) => {
    updateElement(selectedElement.id, {
      attribute_binding: {
        ...selectedElement.attribute_binding,
        meeting_number: e.target.value,
      },
    });
  };

  const handleTextCaseChange = (caseType) => {
    updateElement(selectedElement.id, { textCase: caseType });
  };

  const handleFitTextToContainer = () => {
    if (!selectedElement || selectedElement.type !== "text") return;

    const { content, width, height, fontFamily, fontSize } = selectedElement;

    if (!content || !width || !height) {
      toast.warning("Text element needs content and dimensions to fit text");
      return;
    }

    const optimalFontSize = calculateOptimalFontSizeEnhanced(
      content,
      width,
      height,
      fontFamily,
      fontSize || 16,
    );

    if (optimalFontSize !== fontSize) {
      updateElement(selectedElement.id, { fontSize: optimalFontSize });
      toast.success(
        `Font size adjusted to ${optimalFontSize}px to fit container`,
      );
    } else {
      toast.info("Text already fits perfectly!");
    }
  };

  // const handleRotateLeft = () => {
  //   const currentRotation = selectedElement.rotation || 0;
  //   const newRotation = ((currentRotation - 90) % 360 + 360) % 360;
  //   console.log('newRotation --> ', newRotation);
  //   updateElement(selectedElement.id, { rotation: newRotation });
  // };

  // const handleRotateRight = () => {
  //   const currentRotation = selectedElement.rotation || 0;
  //   const newRotation = ((currentRotation + 90) % 360 + 360) % 360;
  //   updateElement(selectedElement.id, { rotation: newRotation });
  // };

  if (!selectedElement) {
    return (
      <Box sx={{ width: 256, bgcolor: "#313131", p: 2 }}>
        <Typography variant="body2" sx={{ color: "white" }}>
          Select an element to edit properties
        </Typography>
      </Box>
    );
  }
  const ColorPaletteSelector = ({ selectedColor, onColorChange, title }) => (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, color: "white" }}>
        {title}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: "wrap", gap: 1, alignItems: "start" }}
      >
        {canvasColor.map((color) => (
          <Box
            key={color}
            sx={{
              width: 24,
              height: 24,
              backgroundColor: color,
              border:
                selectedColor === color
                  ? "3px solid #1976d2"
                  : "2px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
              "&:hover": {
                border: "3px solid #90caf9",
              },
            }}
            onClick={() => onColorChange(color)}
          />
        ))}
      </Stack>

      {/* Optional: Show current selected color value */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          color: "rgba(255,255,255,0.7)",
          fontFamily: "monospace",
        }}
      >
        Selected: {selectedColor}
      </Typography>
    </Box>
  );
  const DataBindingSection = () => {
    const darkItemSx = {
      color: "white",
      bgcolor: "#4a4a4a",
      "&:hover": { bgcolor: "#5a5a5a" },
      "&.Mui-selected": {
        bgcolor: "primary.main",
        "&:hover": { bgcolor: "primary.dark" },
      },
    };

    const selectSx = {
      bgcolor: "#4a4a4a",
      color: "white",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "lightgray" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
    };

    const menuProps = {
      PaperProps: {
        sx: {
          bgcolor: "#4a4a4a",
          "& .MuiList-root": { padding: 0 },
          "& .MuiDivider-root": { borderColor: "#666" },
        },
      },
    };

    const renderInternalConfigItems = () =>
      internalConfigs?.flatMap((config) => {
        const configValue = config.InternalConfigurationValue;
        if (configValue && typeof configValue === "object") {
          return Object.keys(configValue).map((key) => (
            <MenuItem
              key={`${config.id}-${key}`}
              value={`${config.InternalConfigurationName}.${key}`}
              sx={darkItemSx}
            >
              {key}
            </MenuItem>
          ));
        }
        return [];
      });

    return (
      <Box sx={{ pt: 2, borderTop: 1, borderColor: "grey.700" }}>
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
          Data Binding
        </Typography>
        {truInkEMMSTemplate ? (
          <>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "white" }}>Data Key</InputLabel>
              <Select
                value={selectedElement.attribute_binding?.data_key || ""}
                label="Data Key"
                onChange={handleDataKeyChange}
                sx={selectSx}
                MenuProps={menuProps}
              >
                <MenuItem value="" sx={darkItemSx}>
                  <em>None</em>
                </MenuItem>

                {MEETING_TEMPLATE_DATABINDING.map((key) => (
                  <MenuItem key={key.value} value={key.value} sx={darkItemSx}>
                    {key.label}
                  </MenuItem>
                ))}

                {internalConfigs?.length > 0 && (
                  <Divider sx={{ my: 1, borderColor: "grey.600" }} />
                )}
                {renderInternalConfigItems()}
              </Select>
            </FormControl>

            {(selectedElement.group === "UPCOMING_MEETING" ||
              selectedElement.group === "UPCOMING_MEETING_CARD") && (
              <TextField
                label="Meeting Number"
                type="number"
                value={
                  selectedElement.attribute_binding?.meeting_number !== undefined
                    ? parseInt(selectedElement.attribute_binding.meeting_number)
                    : ""
                }
                onChange={handleMeetingNumberChange}
                variant="outlined"
                size="small"
                inputProps={{ min: 1 }}
                sx={{
                  mt: 2,
                  bgcolor: "#4a4a4a",
                  "& .MuiInputBase-input": { color: "white" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
                  "& .MuiInputLabel-root": { color: "white" },
                }}
              />
            )}
          </>
        ) : customKeys.length > 0 || internalConfigs?.length > 0 ? (
          <FormControl fullWidth size="small">
            <InputLabel sx={{ color: "white" }}>Data Key</InputLabel>
            <Select
              value={selectedElement.attribute_binding?.data_key || ""}
              label="Data Key"
              onChange={handleDataKeyChange}
              sx={selectSx}
              MenuProps={menuProps}
            >
              <MenuItem value="" sx={darkItemSx}>
                <em>None</em>
              </MenuItem>

              {customKeys.map((key) => (
                <MenuItem key={key} value={key} sx={darkItemSx}>
                  {key}
                </MenuItem>
              ))}

              {customKeys.length > 0 && internalConfigs?.length > 0 && (
                <Divider sx={{ my: 1, borderColor: "grey.600" }} />
              )}
              {renderInternalConfigItems()}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Data Key"
            value={selectedElement.attribute_binding?.data_key || ""}
            onChange={handleDataKeyChange}
            variant="outlined"
            size="small"
            placeholder="e.g., temperature"
            sx={{
              bgcolor: "#4a4a4a",
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
              "& .MuiInputLabel-root": { color: "white" },
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Paper
      ref={rightSideRef}
      elevation={3}
      sx={{
        width: 256,
        bgcolor: "#313131",
        color: "white",
        p: 2,
        overflowY: "auto",
      }}
    >
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Properties
      </Typography>

      {/* Conditionally render properties based on element type */}
      {selectedElement.type === "text" && (
        <Stack spacing={2}>
          <TextField
            label="Text"
            multiline
            rows={3}
            value={selectedElement.content}
            onChange={handleTextChange}
            variant="outlined"
            sx={{
              bgcolor: "#4a4a4a",
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
              "& .MuiInputLabel-root": { color: "white" },
            }}
          />

          <ColorPaletteSelector
            selectedColor={selectedElement.fill || "#000000"}
            onColorChange={handleTextColorChange}
            title="Text Color"
          />

          <TextField
            label="Font Size"
            type="number"
            value={selectedElement.fontSize}
            onChange={handleFontSizeChange}
            variant="outlined"
            size="small"
            sx={{
              bgcolor: "#4a4a4a",
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
              "& .MuiInputLabel-root": { color: "white" },
            }}
          />

          {/* Font Family Select - Fixed styling */}
          <FormControl fullWidth size="small">
            <InputLabel
              sx={{
                color: "white !important",
                "&.Mui-focused": {
                  color: "white !important",
                },
              }}
            >
              Font Family
            </InputLabel>
            <Select
              value={selectedElement.fontFamily || "Arial"}
              label="Font Family"
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  fontFamily: e.target.value,
                })
              }
              renderValue={(selected) => {
                const font = availableFonts.find((f) => f.value === selected);
                return font ? font.label : selected;
              }}
              sx={{
                bgcolor: "#4a4a4a",
                color: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "gray",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "lightgray",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              }}
            >
              {availableFonts.map((font) => (
                <MenuItem
                  key={font.value}
                  value={font.value}
                  sx={{
                    fontFamily: font.value,
                    color: "white",
                    bgcolor: "#4a4a4a",
                    "&:hover": {
                      bgcolor: "#5a5a5a",
                    },
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    },
                  }}
                >
                  {font.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <ToggleButtonGroup
              exclusive
              value={selectedElement.textAlign || "left"}
              onChange={(e, alignment) => handleAlignmentChange(alignment)}
              aria-label="text alignment"
              fullWidth
              sx={{
                display: "flex",
                gap: "6px",
                "& .MuiToggleButton-root": {
                  color: "white",
                  borderColor: "gray",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                  "&:not(.Mui-selected)": {
                    backgroundColor: "#4a4a4a",
                    "&:hover": {
                      backgroundColor: "#5a5a5a",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="left" aria-label="left aligned">
                <FormatAlignLeftIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="center" aria-label="centered">
                <FormatAlignCenterIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="right" aria-label="right aligned">
                <FormatAlignRightIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <ToggleButtonGroup
              exclusive
              value={selectedElement.verticalAlign || "center"}
              onChange={(e, alignment) => {
                if (alignment) handleVerticalAlignmentChange(alignment);
              }}
              fullWidth
              sx={{
                display: "flex",
                gap: "6px",
                "& .MuiToggleButton-root": {
                  color: "white",
                  borderColor: "gray",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": { backgroundColor: "primary.dark" },
                  },
                  padding: "10px",
                  "&:not(.Mui-selected)": {
                    backgroundColor: "#4a4a4a",
                    "&:hover": { backgroundColor: "#5a5a5a" },
                  },
                },
              }}
            >
              <ToggleButton value="top">
                <VerticalAlignTopIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="center">
                <VerticalAlignCenterIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="bottom">
                <VerticalAlignBottomIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ToggleButtonGroup
            exclusive
            aria-label="text formatting"
            sx={{
              gap: 1,
              "& .MuiToggleButton-root": {
                color: "white",
                borderColor: "gray",
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
                "&:not(.Mui-selected)": {
                  backgroundColor: "#4a4a4a",
                  "&:hover": {
                    backgroundColor: "#5a5a5a",
                  },
                },
              },
            }}
          >
            <ToggleButton
              value="bold"
              aria-label="bold"
              onClick={handleBoldToggle}
              selected={selectedElement.fontWeight === "bold"}
            >
              <FormatBoldIcon />
            </ToggleButton>
            <ToggleButton
              value="italic"
              aria-label="italic"
              onClick={handleItalicToggle}
              selected={selectedElement.fontStyle === "italic"}
            >
              <FormatItalicIcon />
            </ToggleButton>
            <ToggleButton
              value="fit-text"
              aria-label="fit text"
              onClick={handleFitTextToContainer}
            >
              <Box
                component="span"
                sx={{
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                A↔
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            exclusive
            value={selectedElement.textCase || "none"}
            onChange={(e, val) => { if (val) handleTextCaseChange(val); }}
            aria-label="text case"
            sx={{
              gap: 1,
              "& .MuiToggleButton-root": {
                color: "white",
                borderColor: "gray",
                flex: 1,
                fontSize: "11px",
                fontWeight: "bold",
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "primary.dark" },
                },
                "&:not(.Mui-selected)": {
                  backgroundColor: "#4a4a4a",
                  "&:hover": { backgroundColor: "#5a5a5a" },
                },
              },
            }}
          >
            <ToggleButton value="uppercase" aria-label="uppercase">
              <CaseUpper size={18} />
            </ToggleButton>
            <ToggleButton value="lowercase" aria-label="lowercase">
              <CaseLower size={18} />
            </ToggleButton>
            <ToggleButton value="sentence" aria-label="sentence case">
              <CaseSensitive size={18} />
            </ToggleButton>
          </ToggleButtonGroup>

          <DataBindingSection />
        </Stack>
      )}

      {selectedElement.type === "image" && (
        <Stack spacing={2} sx={{ p: 2, bgcolor: "#4a4a4a", borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Upload Your Image
          </Typography>
          <Button
            variant="contained"
            component="label"
            sx={{ bgcolor: "green", "&:hover": { bgcolor: "darkgreen" } }}
          >
            Choose File
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              hidden
            />
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={selectedElement.ditheringEnabled !== false}
                onChange={(e) =>
                  updateElement(selectedElement.id, {
                    ditheringEnabled: e.target.checked,
                  })
                }
                color="primary"
              />
            }
            label={
              <Typography variant="caption" sx={{ color: "white" }}>
                Maximize Compatiblity
              </Typography>
            }
            sx={{
              mt: 1,
              "& .MuiFormControlLabel-label": { color: "white" },
            }}
          />
          <DataBindingSection />
        </Stack>
      )}

      {selectedElement.type === "shape" && (
        <Stack spacing={2}>
          <FormControl
            fullWidth
            size="small"
            sx={{
              bgcolor: "#4a4a4a",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
            }}
          >
            <InputLabel sx={{ color: "white" }}>Shape Type</InputLabel>
            <Select
              value={selectedElement.shapeType}
              label="Shape Type"
              onChange={handleShapeTypeChange}
              sx={{ color: "white" }}
            >
              <MenuItem value="rectangle">Rectangle</MenuItem>
              <MenuItem value="circle">Circle</MenuItem>
            </Select>
          </FormControl>
          <ColorPaletteSelector
            selectedColor={selectedElement.fill || "#FF0000"}
            onColorChange={handleColorChange}
            title="Shape Color"
          />
        </Stack>
      )}

      {selectedElement.type === "qr" && (
        <Stack spacing={2}>
          <TextField
            label="URL"
            type="text"
            value={selectedElement.url}
            onChange={handleUrlChange}
            placeholder="https://example.com"
            variant="outlined"
            size="small"
            sx={{
              bgcolor: "#4a4a4a",
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
              "& .MuiInputLabel-root": { color: "white" },
            }}
          />

          <ColorPaletteSelector
            selectedColor={selectedElement.fill || "#000000"}
            onColorChange={handleQRColorChange}
            title="QR Color"
          />
        </Stack>
      )}

      {selectedElement.type === "line" && (
        <Stack spacing={2}>
          <FormControl
            fullWidth
            size="small"
            sx={{
              bgcolor: "#4a4a4a",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
            }}
          >
            <InputLabel sx={{ color: "white" }}>Direction</InputLabel>
            <Select
              value={selectedElement.direction}
              label="Line Direction"
              onChange={(e) =>
                updateElement(selectedElement.id, { direction: e.target.value })
              }
              sx={{ color: "white" }}
            >
              <MenuItem value="horizontal">Horizontal</MenuItem>
              <MenuItem value="vertical">Vertical</MenuItem>
            </Select>
          </FormControl>
          <ColorPaletteSelector
            selectedColor={selectedElement.fill || "#000000"}
            onColorChange={(color) =>
              updateElement(selectedElement.id, { fill: color })
            }
            title="Line Color"
          />
        </Stack>
      )}
      {selectedElement.type === "labels" && (
        <Stack spacing={2}>

          <Box>
            <Typography variant="caption" sx={{ color: "white", mb: 0.5, display: "block" }}>
              Layout Direction
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.direction || "row"}
              exclusive
              onChange={(_, val) => {
                if (val) updateElement(selectedElement.id, { direction: val });
              }}
              size="small"
              fullWidth
              sx={{
                "& .MuiToggleButton-root": { color: "white", borderColor: "gray", flex: 1, textTransform: "none", fontSize: "0.75rem" },
                "& .Mui-selected": { bgcolor: "#4a4a4a !important", color: "white" },
              }}
            >
              <ToggleButton value="row">Row</ToggleButton>
              <ToggleButton value="column">Column</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            label={(selectedElement.direction || "row") === "row" ? "Max Per Row" : "Max Per Column"}
            type="number"
            size="small"
            value={selectedElement.maxCount ?? ""}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                maxCount: e.target.value === "" ? undefined : Number(e.target.value),
              })
            }
            inputProps={{ min: 1 }}
            variant="outlined"
            sx={{
              bgcolor: "#4a4a4a",
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
              "& .MuiInputLabel-root": { color: "white" },
            }}
          />

          <Box>
            <Typography variant="caption" sx={{ color: "white", mb: 0.5, display: "block" }}>
              Alignment
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.alignment || "left"}
              exclusive
              onChange={(_, val) => {
                if (val) updateElement(selectedElement.id, { alignment: val });
              }}
              size="small"
              sx={{
                "& .MuiToggleButton-root": { color: "white", borderColor: "gray" },
                "& .Mui-selected": { bgcolor: "#4a4a4a !important", color: "white" },
              }}
            >
              <ToggleButton value="left" aria-label="left">
                <FormatAlignLeftIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="center" aria-label="center">
                <FormatAlignCenterIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="right" aria-label="right">
                <FormatAlignRightIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={selectedElement.ditheringEnabled ?? false}
                onChange={(e) =>
                  updateElement(selectedElement.id, { ditheringEnabled: e.target.checked })
                }
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "white" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "gray" },
                }}
              />
            }
            label={<Typography variant="caption" sx={{ color: "white" }}>Dithering Enabled</Typography>}
          />
        </Stack>
      )}

      {/* === Group Assignment Section === */}
      <Box sx={{ pt: 2, borderTop: 1, borderColor: "grey.700", mt: 2 }}>
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
          Group Assignment
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: "white" }}>Group</InputLabel>
          <Select
            value={selectedElement.group || ""}
            label="Group"
            onChange={(e) => {
              updateElement(selectedElement.id, { group: e.target.value });
            }}
            sx={{
              bgcolor: "#4a4a4a",
              color: "white",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "lightgray",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            }}
          >
            {AVAILABLE_GROUPS.map((group) => (
              <MenuItem key={group.value} value={group.value}>
                {group.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Stack spacing={1} sx={{ mt: 3 }}>
        {/* <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleRotateLeft}
            sx={{ color: "white", borderColor: "gray", flex: 1 }}
            startIcon={<RotateLeftIcon />}
          >
            Rotate Left
          </Button>
          <Button
            variant="outlined"
            onClick={handleRotateRight}
            sx={{ color: "white", borderColor: "gray", flex: 1 }}
            startIcon={<RotateRightIcon />}
          >
            Rotate Right
          </Button>
        </Box> */}
        <Button
          variant="outlined"
          onClick={() => onDuplicate(selectedElement.id)}
          sx={{ color: "white", borderColor: "gray" }}
        >
          Duplicate
        </Button>
        <Button
          variant="outlined"
          onClick={() => onBringForward(selectedElement.id)}
          sx={{ color: "white", borderColor: "gray" }}
        >
          Bring Forward
        </Button>
        <Button
          variant="outlined"
          onClick={() => onSendBackward(selectedElement.id)}
          sx={{ color: "white", borderColor: "gray" }}
        >
          Send Backward
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => onDeleteElement(selectedElement.id)}
          startIcon={<DeleteIcon />}
        >
          Delete
        </Button>
      </Stack>
    </Paper>
  );
};

export default RightSidebar;
